// Shared shapes + merge helper for the GET /orders/{id} response.
//
// The backend's `ordersMgmtDetailResp` (main-api-webhooks/internal/http/modules/
// orders/orders.go) returns `order_breakdown`, `platform_fee`, `refunded_amount`,
// `remaining_amount`, and `refunds` as TOP-LEVEL keys — siblings of `order`,
// never nested inside it. The published @xeppt/xeppt-sdk's TGetOrderById type
// doesn't model any of these fields yet (SDK lag), and neither does its nested
// TOrder — so a locally-extended order type that declares them on the NESTED
// order (as both view_order/index.tsx and developers/orders/index.tsx once
// did) reads as `undefined` at runtime, because the backend never nests them
// there. That silently disabled the Refund button after a partial refund and
// hid the refund summary/breakdown (fixed 2026-07-13).
//
// mergeOrderDetail() is the one place that correctly relocates these fields
// onto the nested order — consumers should never read them directly off a raw
// detail envelope's `order` sub-object.

export type TOrderBreakdown = {
    subtotal: string;
    shipping: string;
    fees: string;
    total: string;
};

export type TRefundEntry = {
    id: string;
    amount: string;
    fee: string;
    currency?: string;
    status: string;
    reason: string;
    points: number;
    created_at: string;
};

// The subset of ordersMgmtDetailResp's fields that live at the TOP level of
// the detail envelope (siblings of `order`), never nested inside it.
export type TOrderDetailTopLevelFields = {
    order_breakdown?: TOrderBreakdown | null;
    platform_fee?: string;
    refunded_amount?: string;
    remaining_amount?: string;
    refunds?: TRefundEntry[];
};

// Merges a detail envelope's top-level refund/breakdown fields onto its
// nested `order`, returning one object every downstream
// `order.remaining_amount` / `order.refunded_amount` / `order.refunds` /
// `order.order_breakdown` read can resolve correctly against. Call this ONCE,
// right after casting the raw GET /orders/{id} response.
export function mergeOrderDetail<TDetail extends TOrderDetailTopLevelFields & { order: object }>(
    detail: TDetail
): TDetail['order'] & TOrderDetailTopLevelFields {
    return {
        ...detail.order,
        order_breakdown: detail.order_breakdown,
        platform_fee: detail.platform_fee,
        refunded_amount: detail.refunded_amount,
        remaining_amount: detail.remaining_amount,
        refunds: detail.refunds
    };
}
